import api from "../services/api";

const MARKETING_SESSION_KEY = "toora-marketing-session";
const CAMPAIGN_KEY = "toora-marketing-campaign";

const createVisitorUuid = () =>
  window.crypto?.randomUUID?.() ||
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    return (char === "x" ? random : (random & 3) | 8).toString(16);
  });

export const getStoredConsentPreferences = () => {
  try {
    if (window.localStorage.getItem("cookieConsent") !== "true") return null;
    const preferences = JSON.parse(
      window.localStorage.getItem("cookiePreferences") || "{}",
    );
    return {
      functional: preferences.functional === true,
      analytics: preferences.analytics === true,
      marketing: preferences.marketing === true,
    };
  } catch (error) {
    console.warn("Could not read stored cookie preferences:", error);
    return null;
  }
};

export const persistMarketingConsent = (preferences) =>
  api.post("/api/marketing/consent", {
    visitorSessionId: getSessionId(),
    preferences: {
      functional: preferences.functional === true,
      analytics: preferences.analytics === true,
      marketing: preferences.marketing === true,
    },
  });

export const hasMarketingConsent = () => {
  const preferences = getStoredConsentPreferences();
  return preferences?.analytics === true || preferences?.marketing === true;
};

const hasAnalyticsConsent = () => {
  try {
    return (
      window.localStorage.getItem("cookieConsent") === "true" &&
      JSON.parse(window.localStorage.getItem("cookiePreferences") || "{}")
        .analytics === true
    );
  } catch {
    return false;
  }
};

const hasAdvertisingConsent = () => {
  try {
    return (
      window.localStorage.getItem("cookieConsent") === "true" &&
      JSON.parse(window.localStorage.getItem("cookiePreferences") || "{}")
        .marketing === true
    );
  } catch {
    return false;
  }
};

const getSessionId = () => {
  try {
    let id = window.sessionStorage.getItem(MARKETING_SESSION_KEY);
    if (!id) {
      id = createVisitorUuid();
      window.sessionStorage.setItem(MARKETING_SESSION_KEY, id);
    }
    return id;
  } catch {
    return createVisitorUuid();
  }
};

export const getMarketingVisitorSessionId = getSessionId;

const getCampaign = () => {
  const query = new URLSearchParams(window.location.search);
  const current = {
    source: query.get("utm_source") || "",
    medium: query.get("utm_medium") || "",
    name: query.get("utm_campaign") || "",
    content: query.get("utm_content") || "",
    term: query.get("utm_term") || "",
  };
  if (
    current.source ||
    current.medium ||
    current.name ||
    current.content ||
    current.term
  ) {
    try {
      window.sessionStorage.setItem(CAMPAIGN_KEY, JSON.stringify(current));
    } catch {
      return current;
    }
    return current;
  }
  try {
    return JSON.parse(window.sessionStorage.getItem(CAMPAIGN_KEY) || "{}");
  } catch {
    return {};
  }
};

const loadScript = (id, src) => {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  script.onerror = () => console.warn(`Marketing tag could not load: ${id}`);
  document.head.appendChild(script);
};

export const loadConsentedMarketingTags = (settings) => {
  const analyticsAllowed = hasAnalyticsConsent();
  const marketingAllowed = hasAdvertisingConsent();
  const config = settings || {};

  if (marketingAllowed && config.MetaPixelID) {
    window.fbq =
      window.fbq ||
      function () {
        (window.fbq.callMethod
          ? window.fbq.callMethod
          : window.fbq.queue.push.bind(window.fbq.queue)).apply(
          window.fbq,
          arguments,
        );
      };
    window.fbq.queue = window.fbq.queue || [];
    window.fbq("init", config.MetaPixelID);
    loadScript("toora-meta-pixel", "https://connect.facebook.net/en_US/fbevents.js");
  }

  if (marketingAllowed && config.TikTokPixelID) {
    window.ttq = window.ttq || [];
    window.ttq.track = window.ttq.track || ((...args) => window.ttq.push(["track", ...args]));
    window.ttq.page = window.ttq.page || (() => window.ttq.push(["page"]));
    loadScript("toora-tiktok-pixel", `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${encodeURIComponent(config.TikTokPixelID)}&lib=ttq`);
  }

  if (analyticsAllowed && config.GA4MeasurementID) {
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer.push(arguments);
      };
    window.gtag("js", new Date());
    window.gtag("config", config.GA4MeasurementID, { send_page_view: false });
    loadScript(
      "toora-google-gtag",
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.GA4MeasurementID)}`,
    );
  }

  if (marketingAllowed && config.GoogleTagID) {
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer.push(arguments);
      };
    window.gtag("js", new Date());
    window.gtag("config", config.GoogleTagID, { send_page_view: false });
    loadScript(
      "toora-google-gtag",
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.GoogleTagID)}`,
    );
  }

  if (marketingAllowed && config.GoogleTagManagerID) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    loadScript(
      "toora-google-tag-manager",
      `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(config.GoogleTagManagerID)}`,
    );
  }

  if (marketingAllowed && config.LinkedInPartnerID) {
    window._linkedin_partner_id = config.LinkedInPartnerID;
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(config.LinkedInPartnerID);
    loadScript(
      "toora-linkedin-insight",
      "https://snap.licdn.com/li.lms-analytics/insight.min.js",
    );
  }

  if (marketingAllowed && config.PinterestTagID) {
    window.pintrk =
      window.pintrk ||
      function () {
        window.pintrk.queue = window.pintrk.queue || [];
        window.pintrk.queue.push(Array.from(arguments));
      };
    window.pintrk("load", config.PinterestTagID);
    loadScript("toora-pinterest-tag", "https://s.pinimg.com/ct/core.js");
  }

  if (marketingAllowed && config.SnapchatPixelID) {
    window.snaptr =
      window.snaptr ||
      function () {
        window.snaptr.handleRequest
          ? window.snaptr.handleRequest.apply(window.snaptr, arguments)
          : window.snaptr.queue.push(arguments);
      };
    window.snaptr.queue = window.snaptr.queue || [];
    window.snaptr("init", config.SnapchatPixelID);
    loadScript("toora-snapchat-pixel", "https://sc-static.net/scevent.min.js");
  }

  if (marketingAllowed && config.MicrosoftUETID) {
    window.uetq = window.uetq || [];
    loadScript(
      "toora-microsoft-uet",
      `https://bat.bing.com/bat.js?ti=${encodeURIComponent(config.MicrosoftUETID)}`,
    );
  }
};

const sendBrowserEvent = (eventName, data) => {
  const advertisingAllowed = hasAdvertisingConsent();
  const analyticsAllowed = hasAnalyticsConsent();
  if (!advertisingAllowed && !analyticsAllowed) return;
  const config = window.__tooraMarketingConfig || {};
  if (advertisingAllowed && config.MetaPixelID && window.fbq) {
    const metaEvents = {
      page_view: "PageView",
      view_item: "ViewContent",
      add_to_cart: "AddToCart",
      begin_checkout: "InitiateCheckout",
      purchase: "Purchase",
    };
    if (metaEvents[eventName]) window.fbq("track", metaEvents[eventName], data);
  }
  if (advertisingAllowed && config.TikTokPixelID && window.ttq) {
    const tikTokEvents = {
      page_view: "PageView",
      view_item: "ViewContent",
      add_to_cart: "AddToCart",
      begin_checkout: "InitiateCheckout",
      purchase: "CompletePayment",
    };
    if (eventName === "page_view") window.ttq.page?.();
    else if (tikTokEvents[eventName]) window.ttq.track?.(tikTokEvents[eventName], data);
  }
  if (advertisingAllowed && config.GoogleTagID && window.gtag) {
    const googleEvents = {
      page_view: "page_view",
      view_item: "view_item",
      add_to_cart: "add_to_cart",
      begin_checkout: "begin_checkout",
      purchase: "purchase",
    };
    if (googleEvents[eventName]) window.gtag("event", googleEvents[eventName], data);
  }
  if (analyticsAllowed && config.GA4MeasurementID && window.gtag) {
    const googleEvents = {
      page_view: "page_view",
      view_item: "view_item",
      add_to_cart: "add_to_cart",
      begin_checkout: "begin_checkout",
      purchase: "purchase",
    };
    if (googleEvents[eventName]) window.gtag("event", googleEvents[eventName], data);
  }
  if (advertisingAllowed && config.PinterestTagID && window.pintrk) {
    const pinterestEvents = {
      page_view: "pagevisit",
      view_item: "pagevisit",
      add_to_cart: "addtocart",
      begin_checkout: "checkout",
      purchase: "checkout",
    };
    if (eventName === "page_view") window.pintrk("page");
    else if (pinterestEvents[eventName]) window.pintrk("track", pinterestEvents[eventName], data);
  }
  if (advertisingAllowed && config.SnapchatPixelID && window.snaptr) {
    const snapchatEvents = {
      page_view: "PAGE_VIEW",
      view_item: "VIEW_CONTENT",
      add_to_cart: "ADD_CART",
      begin_checkout: "START_CHECKOUT",
      purchase: "PURCHASE",
    };
    if (snapchatEvents[eventName]) window.snaptr("track", snapchatEvents[eventName], data);
  }
  if (advertisingAllowed && config.MicrosoftUETID && window.uetq) {
    const microsoftEvents = {
      add_to_cart: "add_to_cart",
      begin_checkout: "begin_checkout",
      purchase: "purchase",
    };
    if (microsoftEvents[eventName]) window.uetq.push("event", microsoftEvents[eventName], data);
  }
  if (config.LinkedInPartnerID && window.lintrk && eventName === "purchase") {
    window.lintrk("track", { conversion_id: data.conversion_id });
  }
};

export const trackMarketingEvent = async (eventName, details = {}) => {
  if (!hasMarketingConsent()) return;

  const campaign = getCampaign();
  const browserEvent = {
    currency: details.currency || "ZAR",
    value: Number.isFinite(Number(details.value)) ? Number(details.value) : undefined,
    content_name: details.productName,
    content_ids: details.productId ? [details.productId] : undefined,
    quantity: details.quantity,
  };

  if (eventName === "purchase") {
    if (details.orderId) {
      const purchaseKey = `toora-marketing-purchase:${details.orderId}`;
      try {
        if (window.sessionStorage.getItem(purchaseKey) === "sent") return;
      } catch {
        // The server's unique event key remains the durable deduplication guard.
      }
      try {
        await api.post("/api/marketing/purchase", {
          orderId: details.orderId,
          campaign,
          visitorSessionId: getSessionId(),
        });
        try {
          window.sessionStorage.setItem(purchaseKey, "sent");
        } catch {
          // The server's unique event key remains the durable deduplication guard.
        }
        sendBrowserEvent(eventName, browserEvent);
      } catch (error) {
        console.warn("Could not record verified marketing purchase:", error);
      }
    }
    return;
  }

  sendBrowserEvent(eventName, browserEvent);
  try {
    await api.post("/api/marketing/events", {
      eventId:
        window.crypto?.randomUUID?.() ||
        `event-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      eventName,
      visitorSessionId: getSessionId(),
      pagePath: window.location.pathname,
      campaign,
      productId: details.productId,
      productName: details.productName,
      quantity: details.quantity,
    });
  } catch (error) {
    console.warn(`Could not record marketing ${eventName} event:`, error);
  }
};

export const setTrackingConfig = (settings) => {
  window.__tooraMarketingConfig = settings || {};
  if (!hasMarketingConsent()) {
    window.fbq?.("consent", "revoke");
    window.gtag?.("consent", "update", {
      ad_storage: "denied",
      analytics_storage: hasAnalyticsConsent() ? "granted" : "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    window.ttq?.disableCookie?.();
    [
      "toora-meta-pixel",
      "toora-tiktok-pixel",
      "toora-google-tag-manager",
      "toora-linkedin-insight",
      "toora-pinterest-tag",
      "toora-snapchat-pixel",
      "toora-microsoft-uet",
    ].forEach((id) => document.getElementById(id)?.remove());
    if (!hasAnalyticsConsent()) {
      document.getElementById("toora-google-gtag")?.remove();
    }
  }
  loadConsentedMarketingTags(settings);
};
